import { useState } from 'react';
import { Order } from '../portals/admin/types';

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedOrder, setEditedOrder] = useState<Partial<Order>>({});

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/orders/manage', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    const order = orders.find(o => o._id === orderId);
    const currentStatus = order?.status || 'unknown';

    if (currentStatus === newStatus) {
      return;
    }

    const statusLabels: { [key: string]: string } = {
      pending: 'Pending',
      assigned: 'Assigned',
      planted: 'Planted',
      completed: 'Completed'
    };

    const confirmMessage = `Change order status from "${statusLabels[currentStatus] || currentStatus}" to "${statusLabels[newStatus] || newStatus}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        loadOrders();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update order status');
    }
  };

  const saveOrderChanges = async () => {
    if (!selectedOrder || !editedOrder) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/orders/${selectedOrder._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedOrder)
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setSelectedOrder(updatedOrder.order || updatedOrder);
        setIsEditing(false);
        loadOrders();
        alert('Order updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to update order');
      }
    } catch (error) {
      console.error('Failed to save order changes:', error);
      alert('Failed to update order');
    }
  };

  const viewOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setEditedOrder(order);
    setShowOrderModal(true);
    setIsEditing(false);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
    setIsEditing(false);
    setEditedOrder({});
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditedOrder({ ...selectedOrder });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedOrder({ ...selectedOrder });
  };

  const handleEditChange = (field: string, value: any) => {
    setEditedOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return {
    orders,
    selectedOrder,
    showOrderModal,
    isEditing,
    editedOrder,
    loadOrders,
    updateOrderStatus,
    saveOrderChanges,
    viewOrderDetails,
    closeOrderModal,
    startEditing,
    cancelEditing,
    handleEditChange
  };
};
